import { AnimatePresence, motion } from 'framer-motion';

import { OrgPermission } from '../../../../../backend/src/organisation/organisation-permission';
import { CountBadge } from '../count-badge';
import { NewChip } from '../new-chip';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Switch } from '@/new/components/shadcn/switch';

interface ImportSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unpackEmail: boolean;
  onUnpackEmailChange: (unpackEmail: boolean) => void;
  deduplicateDocuments: boolean;
  onDeduplicateDocumentsChange: (deduplicateDocuments: boolean) => void;
  fallbackToExtractedText: boolean;
  onFallbackToExtractedTextChange: (fallbackToExtractedText: boolean) => void;
}

export function ImportSettingsDialog({
  open,
  onOpenChange,
  unpackEmail,
  onUnpackEmailChange,
  deduplicateDocuments,
  onDeduplicateDocumentsChange,
  fallbackToExtractedText,
  onFallbackToExtractedTextChange,
}: ImportSettingsDialogProps) {
  const { isEnabled } = useOrgPermissions();
  const showRelativityOptions = isEnabled(OrgPermission.RelativityUsage);

  const settingsCount = [!unpackEmail, !deduplicateDocuments, showRelativityOptions && fallbackToExtractedText].reduce(
    (a, v) => a + (v ? 1 : 0),
    0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Icon className="lucide--settings-2" /> Import Settings
          {settingsCount > 0 ? (
            <CountBadge
              count={settingsCount}
              className="size-4.25 border-sky-300 bg-sky-50 p-0 text-[10px] font-semibold text-sky-400"
            />
          ) : (
            showRelativityOptions && <NewChip className="-right-2.5" />
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--settings-2" /> Import Settings
          </DialogTitle>
          <DialogDescription>
            Configure settings for this upload. Changes will not affect uploads that have already been completed. These
            settings are not saved or persisted anywhere beyond this session.
          </DialogDescription>
        </DialogHeader>

        <div className="mx-auto my-14 flex max-w-lg flex-col gap-10">
          <div className="flex items-center gap-8">
            <Switch
              id="email-unpacking"
              checked={!unpackEmail}
              onCheckedChange={(value) => onUnpackEmailChange(!value)}
            />

            <div className="space-y-2">
              <label htmlFor="email-unpacking" className="block font-medium text-gray-800">
                Disable email unpacking
              </label>

              <p className="text-gray-500">
                When enabled, <kbd>.eml</kbd> and <kbd>.msg</kbd> files won't have their attachments extracted into
                their own documents. Useful when attachments have already been extracted.
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-8">
              <Switch
                id="deduplication"
                checked={!deduplicateDocuments}
                onCheckedChange={(value) => onDeduplicateDocumentsChange(!value)}
              />

              <div className="space-y-2">
                <label htmlFor="deduplication" className="block font-medium text-gray-800">
                  Disable deduplication
                </label>

                <p className="text-gray-500">
                  When enabled, uploaded documents will <strong className="text-gray-700">not</strong> be deduplicated
                  against each other, or documents already in this Matter. Every uploaded document will be submitted for
                  processing.
                </p>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {!deduplicateDocuments && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={{
                    hidden: {
                      opacity: 0,
                      height: 0,
                      transition: {
                        opacity: { duration: 0.1 },
                        height: { duration: 0.12, delay: 0.07 },
                      },
                    },
                    visible: {
                      opacity: 1,
                      height: 'auto',
                      transition: {
                        height: { duration: 0.12 },
                        opacity: { duration: 0.12, delay: 0.07 },
                      },
                    },
                  }}
                  className="ml-15 overflow-hidden"
                >
                  <div className="mt-4 flex gap-2.25 rounded-md border border-amber-200 bg-amber-50 px-3.25 py-2.5 text-[13px] text-amber-600">
                    <Icon className="lucide--alert-triangle mt-0.75 text-amber-500" />
                    <p>
                      Warning: Facts may not always be consolidated across duplicate documents, making it possible for
                      the same or similar facts to appear multiple times. This can influence Kim's behaviour when
                      reasoning about these facts. Be certain before enabling this setting.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {showRelativityOptions && (
            <div className="flex items-center gap-8">
              <Switch
                id="fallback-to-extracted-text"
                checked={fallbackToExtractedText}
                onCheckedChange={onFallbackToExtractedTextChange}
              />

              <div className="space-y-2">
                <label
                  htmlFor="fallback-to-extracted-text"
                  className="flex items-center gap-2 font-medium text-gray-800"
                >
                  <NewChip className="relative top-auto right-auto" />
                  Fallback to extracted text
                  <Badge className="bg-purple-50 text-purple-600">Relativity</Badge>
                </label>

                <p className="text-gray-500">
                  When enabled, files with formats Wexler doesn't natively support will fall back to using the OCR'd
                  text from Relativity. If no text is available, the file will be marked as unsupported.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
